// articles/PlayerGuide.js
// "Player Guide" (Sept 28, 2026). For golfers in a TTO group, written to "you".
// Reached from the 📖 Player Guide button in the player app (?from=player) and from
// the organizer's 📖 Guides chooser (?from=app); organizers can also send the link.
// Features the group's plan must include are tagged [Paid Subscriptions] (Dennis,
// Sept 28): scoring, posting to GHIN, My Rounds, full stats, text messages and
// automatic handicap updates. Games are left out until their plan level is decided.
// Labels are quoted from the live player app (App.js PlayerPortal and the invite
// page); if one changes, change it here too. Pictures are rendered from the real
// components with invented sample players (public/guides/player and /scoring).

import React from 'react';
import { colors, getGuideBySlug } from '../guidesData';
import { H2, H3, P, UL, LI, InlineLink, Callout, GuideCTA, FAQ } from '../articleUI';
import { Step, Btn, Heads, StepTracker, PhoneShot, PhoneRow, PRINT_CSS } from './eventGuideParts';

const guide = getGuideBySlug('tee-time-organizer-player-guide');

// The notation Dennis chose for anything the group's plan must include.
function Paid() {
  return (
    <span
      style={{
        display: 'inline-block',
        fontSize: 12,
        fontWeight: 700,
        color: '#92400e',
        background: '#fef3c7',
        border: '1px solid #fcd34d',
        borderRadius: 6,
        padding: '0 6px',
        margin: '0 4px',
        whiteSpace: 'nowrap',
        verticalAlign: 'middle',
        lineHeight: '20px',
      }}
    >
      [Paid Subscriptions]
    </span>
  );
}

const FAQS = [
  {
    q: 'Do I need to download an app or make a password?',
    a: 'No. Your app opens from the link in any Tee Time Organizer email, and your phone remembers it. Add it to your home screen and it opens like any other app.',
  },
  {
    q: 'I lost my link. How do I get back in?',
    a: 'Open any recent email from Tee Time Organizer about a tee time and tap the link in it. If you can’t find one, ask your organizer to send you one.',
  },
  {
    q: 'Why don’t I see Scoring or My Rounds?',
    a: 'They are marked [Paid Subscriptions] in this guide: your group’s plan decides whether you have them. If you see them with a 🔒, the plan doesn’t include them yet.',
  },
  {
    q: 'How do I change my email address?',
    a: 'Ask your organizer. Your email is how Tee Time Organizer knows you, so only the organizer can change it.',
  },
  {
    q: 'I play in two groups. Do I need two apps?',
    a: 'No. Link the second group from Edit Profile (🔗 Link to another Tee Time Organizer group) and its tee times appear in the same app.',
  },
  {
    q: 'Does Tee Time Organizer post my score to GHIN for me?',
    a: 'No. You post it yourself, with the Post to GHIN button on your card or on ghin.com. Tee Time Organizer never posts on your behalf.',
  },
];

export default function PlayerGuide() {
  return (
    <article>
      <h1
        style={{
          fontFamily: "'DM Serif Display', Georgia, serif",
          fontSize: 38,
          fontWeight: 400,
          color: colors.gray900,
          margin: '0 0 8px',
          lineHeight: 1.15,
        }}
      >
        Player Guide
      </h1>
      <style>{PRINT_CSS}</style>
      <div
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', margin: '0 0 28px' }}
      >
        <p style={{ fontSize: 15, color: colors.gray400, margin: 0 }}>
          {guide.readMinutes} min read &middot; For golfers in a Tee Time Organizer group
        </p>
        <button
          type="button"
          className="guide-no-print"
          onClick={() => window.print()}
          style={{
            background: 'white',
            color: colors.green800,
            border: `1px solid ${colors.gray300}`,
            borderRadius: 8,
            padding: '8px 14px',
            fontSize: 14,
            fontWeight: 700,
            cursor: 'pointer',
          }}
        >
          🖨️ Print this guide
        </button>
      </div>

      <P>
        Your group uses Tee Time Organizer to run its tee times. This guide shows you how to use
        your app: getting in, setting up your profile, reserving a spot, seeing who you&rsquo;re
        playing with, joining events, and keeping score.
      </P>

      <Callout>
        <strong>About <Paid /></strong>
        <P>
          Items marked <Paid /> are included when your group&rsquo;s organizer has a paid Tee Time
          Organizer plan. If you don&rsquo;t see one of them in your app, or it shows 🔒, your
          group&rsquo;s plan doesn&rsquo;t include it. Everything else works for every group.
        </P>
      </Callout>

      <StepTracker
        storageKey="tto_guide_player_steps"
        steps={[
          'Open your app',
          'Set up your profile',
          'Choose how you hear from us',
          'Reserve a spot',
          'Your upcoming golf',
          'Bring your playing partners',
          'Events and tournaments',
          'Score your round',
          'Post your score to GHIN',
          'My Rounds and Stats',
        ]}
        note="Steps 1 to 3 are done once. The rest are there whenever you need them. Tap a step to jump to it."
      />

      <Step n={1} title="Open your app" when="Once">
        <PhoneShot
          src="/guides/player/home.webp"
          alt="The top of the player app: Welcome, Jim, with buttons for Stats, Scoring, Share, My Rounds, Edit Profile, What's New and Player Guide"
          caption="The top of your app. Scoring and My Rounds show when your group has them."
        />
        <UL>
          <LI>
            <strong>Your link is in every email</strong> Tee Time Organizer sends you about a tee
            time. Tap it and your app opens. There&rsquo;s nothing to download and no password.
          </LI>
          <LI>
            <strong>Put it on your mobile phone&rsquo;s home screen</strong> so it opens like any
            other app. If you see the green <strong>Add to Home Screen for quick access</strong>{' '}
            bar, tap <Btn>Show Me</Btn>. Otherwise:
            <UL>
              <LI>
                <strong>iPhone (Safari):</strong> tap the <strong>Share</strong> button at the bottom,
                then <strong>Add to Home Screen</strong>, then <strong>Add</strong>.
              </LI>
              <LI>
                <strong>Android (Chrome):</strong> tap the <strong>⋮</strong> menu at the top right,
                then <strong>Add to Home Screen</strong>, then <strong>Add</strong>.
              </LI>
            </UL>
          </LI>
          <LI>
            <strong>The buttons at the top:</strong> <Btn>📊 Stats</Btn>, <Btn>⛳ Scoring</Btn>
            <Paid />, <Btn>📤 Share</Btn>, <Btn>🏌️ My Rounds</Btn>
            <Paid />, <Btn>✏️ Edit Profile</Btn>, <Btn>What&rsquo;s New</Btn> (news about the app)
            and <Btn>📖 Player Guide</Btn> (this guide).
          </LI>
          <LI>
            Below them: <strong>📅 My Upcoming Golf</strong> (the tee times you&rsquo;re in) and{' '}
            <strong>🏌️ Available Tee Times</strong> (the ones you can join).
          </LI>
        </UL>
      </Step>

      <Step n={2} title="Set up your profile" when="Once, and when your handicap changes">
        <P>
          Tap <Btn>✏️ Edit Profile</Btn>. Your handicap and tee let Tee Time Organizer work out your
          course handicap, so your pairings, net scores and results are right.
        </P>
        <UL>
          <LI>
            <strong>Profile Photo:</strong> optional, but it helps the group put a face to the name.
          </LI>
          <LI>
            <strong>Phone</strong> and <strong>GHIN Number</strong>.
          </LI>
          <LI>
            <strong>Handicap:</strong> your current Handicap Index, for example 12.4.{' '}
            <strong>🔗 Verify on GHIN.com</strong> opens GHIN so you can check it. If it hasn&rsquo;t
            been updated in a while, a banner on your app reminds you.
          </LI>
          <LI>
            <strong>⛳ Preferred Tee:</strong> the tees you usually play, and{' '}
            <strong>Tees you play</strong> (Men&rsquo;s or Women&rsquo;s), which sets the course
            rating used for your handicap.
          </LI>
          <LI>
            Tap <Btn>Save Changes</Btn>.
          </LI>
          <LI>
            <strong>Email:</strong> to change it, ask your organizer.
          </LI>
        </UL>
        <PhoneShot
          src="/guides/player/profile.webp"
          alt="Edit Profile: Handicap 12.4, Connect GHIN to post scores, Email, Preferred Tee White, Tees you play Men's"
          caption="Handicap, GHIN, and your preferred tee in Edit Profile."
        />
        <P>
          <strong>Connect GHIN</strong> <Paid />: tap <Btn>🔗 Connect GHIN to post scores</Btn> and
          enter your ghin.com email and password, so you can post scores from your card (step 9). If
          your group&rsquo;s plan includes automatic handicap updates, the button is{' '}
          <Btn>🔄 Sync Handicap from GHIN</Btn> instead: it keeps your index up to date from GHIN
          every Monday and after the days you play.
        </P>
      </Step>

      <Step n={3} title="Choose how you hear from us" when="Once">
        <P>
          Also in <Btn>✏️ Edit Profile</Btn>, under <strong>Communication Preferences</strong>:
        </P>
        <UL>
          <LI>
            <strong>Email Notifications:</strong> new tee times, your confirmations, reminders
            before you play, and changes. Your organizer decides when reminders go out.
          </LI>
          <LI>
            <strong>Text Messages</strong> <Paid />: the same reminders by text, if your group has
            them.
          </LI>
          <LI>
            <strong>GHIN Posting Reminder:</strong> an email after the round reminding you to post
            your score. It skips you if you&rsquo;ve already posted.
          </LI>
          <LI>
            <strong>Target Score in Reminders:</strong> shows par plus your course handicap in your
            reminder.
          </LI>
        </UL>
        <Heads>
          Your app and your emails use the same link. Keep one recent email and you can always get
          back in.
        </Heads>
      </Step>

      <Step n={4} title="Reserve a spot" when="Whenever a tee time is posted">
        <UL>
          <LI>
            Each tee time under <strong>🏌️ Available Tee Times</strong> shows the course, date and
            time, how many are signed up, the spots left and, within 15 days, the forecast. Tap{' '}
            <Btn>👥 View</Btn> to see who&rsquo;s playing.
          </LI>
          <LI>
            Tap <Btn>Reserve My Spot</Btn>, then <Btn>Confirm</Btn>. It moves to{' '}
            <strong>My Upcoming Golf</strong> as <strong>✓ Confirmed</strong> and you get a
            confirmation email.
          </LI>
          <LI>
            <strong>Full?</strong> Reserve anyway and you go on the waitlist, with your place in line.
            When someone drops out, the first person on the waitlist gets the spot automatically and
            an email saying so.
          </LI>
          <LI>
            You can&rsquo;t hold two tee times that overlap; the app tells you which one to cancel
            first. When your organizer closes a tee time, it stops taking new players.
          </LI>
          <LI>
            The <Btn>📅</Btn> button adds the round to Google Calendar, or downloads a calendar file
            for Apple or Outlook.
          </LI>
        </UL>
      </Step>

      <Step n={5} title="Your upcoming golf" when="Before you play">
        <UL>
          <LI>
            Each round you&rsquo;re in shows <strong>✓ Confirmed</strong> or{' '}
            <strong>⏳ Waitlist</strong>, the players and the forecast.
          </LI>
          <LI>
            Once your organizer sets the pairings, the card shows <strong>Your group</strong>, and
            your starting hole for a shotgun start.
          </LI>
          <LI>
            <Btn>👁️ View Details</Btn> shows any <strong>📋 Note from your organizer</strong>, every
            group with its time, and each player&rsquo;s tees and course handicap. If yours is
            missing, tap <Btn>⛳ Set your tee</Btn> on your own name.
          </LI>
          <LI>
            <strong>Can&rsquo;t make it?</strong> Tap <Btn>Cancel My Spot</Btn> (or{' '}
            <Btn>Leave Waitlist</Btn>) and confirm with <Btn>Remove Me</Btn>. Your organizer is told,
            and the first player on the waitlist gets your spot.
          </LI>
          <LI>
            During the round, <Btn>Live — View Leaderboard</Btn> appears on the card.
          </LI>
        </UL>
        <PhoneRow>
          <PhoneShot
            src="/guides/player/card.webp"
            alt="A confirmed tee time in My Upcoming Golf: Harbor Course, 8:30 AM, forecast, your group of four, View Details and Cancel My Spot"
            caption="A round you&rsquo;re in, with your group."
            width={230}
          />
          <PhoneShot
            src="/guides/player/details.webp"
            alt="View Details: a note from the organizer, then Group 1 at 8:30 and Group 2 at 8:40, each player with White tees and course handicap"
            caption="View Details: the note, the groups, tees and course handicaps."
            width={230}
          />
        </PhoneRow>
      </Step>

      <Step n={6} title="Bring your playing partners" when="If your group allows it">
        <UL>
          <LI>
            If your organizer allows it, <Btn>➕ Add Playing Partners</Btn> appears on your round
            while there are open spots. Pick players from your group and tap{' '}
            <Btn>Send Invites</Btn>. It stops being available close to the round (24 hours before,
            unless your organizer set a different time).
          </LI>
          <LI>
            Each player gets an email and sees the invitation under{' '}
            <strong>✉️ Pending Invitations</strong> in his app, with <Btn>✓ Accept</Btn> and{' '}
            <Btn>Decline</Btn>. Until he answers, he shows faded with ⏳ and &ldquo;Invited&rdquo;.
          </LI>
          <LI>
            If he doesn&rsquo;t answer in time, the spot is released. You get an email when he
            accepts or declines.
          </LI>
        </UL>
      </Step>

      <Step n={7} title="Events and tournaments" when="When your organizer runs one">
        <UL>
          <LI>
            <strong>The invitation:</strong> your organizer may email you an invitation link. It
            opens <strong>You&rsquo;re Invited!</strong> Enter your name and email and tap{' '}
            <Btn>✅ Accept &amp; RSVP</Btn> (or <Btn>🎟️ Join Waitlist</Btn> if it&rsquo;s full).
            You&rsquo;ll see <strong>You&rsquo;re In!</strong> and get a confirmation email. If
            there&rsquo;s a dinner or reception too, you can <Btn>Reserve my place</Btn> for you and
            your guests.
          </LI>
          <LI>
            <strong>Before the day:</strong> the event appears in <strong>My Upcoming Golf</strong>.
            Once teams and pairings are set, <Btn>👁️ View Details</Btn> shows your team or group and
            starting hole. Make sure your handicap and tee are set (step 2).
          </LI>
          <LI>
            <strong>Team events (one card per team):</strong> one player keeps the team&rsquo;s card.
            Scan the QR code on your team&rsquo;s scorecard or cart sign with your phone&rsquo;s
            camera, tap your name under <strong>Who&rsquo;s keeping the card?</strong>, and enter the
            team&rsquo;s score each hole. Teammates can scan the same code to follow along, or take
            over if the scorer&rsquo;s phone dies.
          </LI>
          <LI>
            <strong>Events where everyone plays his own ball</strong> (like Best Ball): you score
            your own card as in step 8.
          </LI>
          <LI>
            Friends and family can follow the live leaderboard at the address on the scorecard.
          </LI>
        </UL>
      </Step>

      <Step n={8} title="Score your round" when="On the course">
        <P>
          <Paid /> Tap <Btn>⛳ Scoring</Btn> and choose how to enter your score:
        </P>
        <UL>
          <LI>
            <strong>📱 Live Round:</strong> hole by hole as you play. One tap enters par;{' '}
            <strong>−</strong> and <strong>+</strong> change it. Putts are optional but feed your
            putting stats.
          </LI>
          <LI>
            <strong>📝 Post-Round Entry — Hole by Hole:</strong> the whole card after the round.
          </LI>
          <LI>
            <strong>🔢 Post-Round Entry — Total Score:</strong> just your totals.
          </LI>
        </UL>
        <PhoneRow>
          <PhoneShot
            src="/guides/scoring/card.webp"
            alt="The live scorecard on the 7th hole with Yards to Center 171 and the running totals"
            caption="The live card: yards to the green, and your totals across the top."
            width={230}
          />
          <PhoneShot
            src="/guides/scoring/board.webp"
            alt="The live leaderboard ranking the players"
            caption="The live leaderboard."
            width={230}
          />
        </PhoneRow>
        <UL>
          <LI>
            The card works out your course handicap, marks the holes where you get a stroke, and
            keeps your gross, net and putts as you go.
          </LI>
          <LI>
            On courses where every green is mapped, it shows <strong>Yards to Center</strong> from
            where you stand. Allow the app to use your location when your phone asks.
          </LI>
          <LI>
            <strong>+ Score for another player</strong> lets you keep the card for up to three
            others in your group.
          </LI>
          <LI>
            <strong>Picked up?</strong> Tap <strong>X</strong>. It counts as a net double bogey.
          </LI>
          <LI>
            <strong>No signal?</strong> Keep scoring. Your scores save as soon as the phone is back
            online.
          </LI>
          <LI>
            <strong>📊 Leaderboard</strong> shows the whole field. If your organizer has turned on
            celebrations, a banner pops up when someone in your tee time makes a birdie or better
            (your organizer picks the level).
          </LI>
        </UL>
        <P>
          For more, see{' '}
          <InlineLink to="/guides/scoring-your-golf-rounds">Scoring Your Rounds</InlineLink>.
        </P>
      </Step>

      <Step n={9} title="Post your score to GHIN" when="After the round">
        <UL>
          <LI>
            <Paid /> Connect GHIN first (step 2). Once 9 or more holes are scored,{' '}
            <Btn>Post to GHIN</Btn> appears on your card. Check the course and tee, and confirm.
          </LI>
          <LI>
            Your card then shows <strong>🔒 GHIN Posted</strong> and the scores lock. Need to fix a
            hole? Do it before you post; after that, correct it on ghin.com.
          </LI>
          <LI>
            You can always post on ghin.com instead. Tee Time Organizer never automatically posts
            for you.
          </LI>
        </UL>
      </Step>

      <Step n={10} title="My Rounds and Stats" when="Any time">
        <UL>
          <LI>
            <Btn>🏌️ My Rounds</Btn> <Paid />: log and score rounds you play outside your group, at
            any course. They count in your stats too.
          </LI>
          <LI>
            <Btn>📊 Stats</Btn>: every group shows how many rounds you&rsquo;ve played, by month or
            quarter.
          </LI>
        </UL>
        <P>
          <Paid /> When your group scores its rounds, <Btn>📊 Stats</Btn> becomes a record of your
          game that builds itself round after round, with nothing to add up or write down.
        </P>
        <PhoneShot
          src="/guides/scoring/stats.webp"
          alt="The Stats overview: scoring average, best and worst rounds, putts, greens in regulation, handicap and scoring by par"
          caption="The Overview tab of your stats."
        />
        <H3>Overview</H3>
        <UL>
          <LI>
            Your <strong>scoring average</strong>, with your last 5 and last 10 rounds, and whether
            you&rsquo;re trending better or worse.
          </LI>
          <LI>
            Your best round, putts and 3-putts per round, greens in regulation, your average on par
            3s, 4s and 5s, and how often you make birdie, par, bogey and worse.
          </LI>
        </UL>
        <H3>Courses: find the holes where you can pick up strokes</H3>
        <UL>
          <LI>
            Every course you&rsquo;ve played, with your rounds, average and best score there.
          </LI>
          <LI>
            Open a course to see <strong>your average on every hole</strong>, against par, beside
            the hole&rsquo;s yardage and handicap, plus your par-3, par-4 and par-5 averages.
          </LI>
          <LI>
            Your <strong>Toughest Hole</strong> and <strong>Strongest Hole</strong> are called out.
            The holes where you average most over par are where strokes are waiting: a different
            club off the tee, a safer target or a layup there can save you more than anywhere else
            on the course.
          </LI>
        </UL>
        <H3>Group: how you stack up in your regular game</H3>
        <UL>
          <LI>
            <strong>Group Leaderboard:</strong> everyone in your group ranked by scoring average,
            gross or net, with rounds played and best round. You see who leads on gross and on net,
            and exactly where you stand.
          </LI>
          <LI>
            <strong>Head to Head:</strong> your win-loss record against each player you&rsquo;ve
            played with, gross or net: rounds together, wins, losses, ties and win percentage.
            Settle the argument about who really has whose number.
          </LI>
        </UL>
        <H3>Wall of Fame</H3>
        <UL>
          <LI>
            Your group&rsquo;s holes-in-one, albatrosses, eagles and rounds at par or better, each
            with the player, course, hole and date.
          </LI>
          <LI>
            They&rsquo;re added automatically as scores are saved, so nobody has to remember to report
            them, and the group&rsquo;s best moments are kept for good.
          </LI>
        </UL>
      </Step>

      <H2>More in your app</H2>
      <UL>
        <LI>
          <strong>Play with another group?</strong> In <Btn>✏️ Edit Profile</Btn>, tap{' '}
          <strong>🔗 Link to another Tee Time Organizer group</strong> and enter that group&rsquo;s
          Organization ID (ask its organizer). Your email must match the one that group has for you.
          Its tee times then appear in your app too.
        </LI>
        <LI>
          <strong>📤 Share:</strong> know someone who runs a golf group? Send them an invitation to
          try Tee Time Organizer.
        </LI>
        <LI>
          <strong>What&rsquo;s New:</strong> a red dot means there&rsquo;s news about the app.
        </LI>
      </UL>

      <FAQ id="player-guide" faqs={FAQS} />
      <div className="guide-no-print">
        <GuideCTA />
      </div>
    </article>
  );
}
